import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { FileCheck2, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { einvoiceApi } from '../services/api';

const Splash = ({ icon: Icon, title, children, testId }) => (
  <Card data-testid={testId} className="card-swiss max-w-2xl mx-auto mt-10">
    <CardContent className="py-12 px-8 text-center space-y-4">
      <Icon className="w-12 h-12 mx-auto text-emerald-700" />
      <h1 className="text-2xl font-bold font-['Manrope']">{title}</h1>
      {children}
    </CardContent>
  </Card>
);

const ACCESS_LABELS = {
  active: ['Fatture attive', 'border-emerald-300 text-emerald-700 bg-emerald-50'],
  trial: ['Fatture in prova', 'border-blue-300 text-blue-700 bg-blue-50'],
  expired: ['Prova fatture scaduta', 'border-red-300 text-red-700 bg-red-50'],
};

export const EinvoiceAccessBadge = ({ status, testId }) => {
  const cfg = ACCESS_LABELS[status?.status];
  if (!cfg) return null;
  return (
    <span data-testid={testId} className={`mt-1 block w-fit whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded border ${cfg[1]}`}>
      {cfg[0]}{status.status === 'trial' ? ` (${status.days_left}g)` : ''}
    </span>
  );
};

export const EinvoiceGate = ({ children }) => {
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { einvoiceApi.getAccess().then((r) => setAccess(r.data)).catch(() => setAccess({ status: 'error' })); }, []);

  const startTrial = async () => {
    setBusy(true);
    try {
      const r = await einvoiceApi.startTrial();
      setAccess(r.data);
      toast.success('Prova gratuita attivata: hai 7 giorni per provare le fatture elettroniche');
    } catch { toast.error('Impossibile attivare la prova'); }
    finally { setBusy(false); }
  };

  if (!access) return <div className="flex items-center justify-center h-64 text-muted-foreground">Caricamento...</div>;

  if (access.status === 'none') {
    return (
      <Splash icon={FileCheck2} title="Fatturazione elettronica" testId="einvoice-trial-offer">
        <p className="text-muted-foreground">Crea fatture dai tuoi DDT o da zero, inviale allo SDI tramite il tuo account Aruba e segui lo stato di consegna, tutto dall'app.</p>
        <p className="text-sm">Provala <b>gratis per 7 giorni</b>. Finita la prova è una funzionalità a pagamento.</p>
        <Button data-testid="start-einvoice-trial-btn" className="btn-primary" onClick={startTrial} disabled={busy}>
          <Sparkles className="w-4 h-4 mr-2" /> Attiva prova gratuita
        </Button>
      </Splash>
    );
  }

  if (access.status !== 'trial' && access.status !== 'active') {
    return (
      <Splash icon={Lock} title="Funzionalità a pagamento" testId="einvoice-locked">
        <p className="text-muted-foreground">La prova gratuita di 7 giorni della fatturazione elettronica è terminata.</p>
        <p className="text-sm">Per continuare a usarla contatta l'amministratore e richiedi l'attivazione.</p>
      </Splash>
    );
  }

  return (
    <>
      {access.status === 'trial' && (
        <div data-testid="einvoice-trial-banner" className="mb-4 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Prova gratuita: {access.days_left === 1 ? 'ultimo giorno' : `ancora ${access.days_left} giorni`} (fino al {new Date(access.trial_ends_at).toLocaleDateString('it-IT')}). Poi la funzione sarà a pagamento.
        </div>
      )}
      {children}
    </>
  );
};

export default EinvoiceGate;
